/**
 * Query param key for the settings modal overlay (`?settings=profile`).
 * Keeps pathname stable for history and tab matching.
 */
export const SETTINGS_SEARCH_PARAM = 'settings'

/**
 * Sub-tab within a settings section for memory-routed pages (`?settings_tab=domain_control`).
 * See {@link ./settings_modal_memory_tabs.js} for slug → path mapping per section.
 */
export const SETTINGS_TAB_SEARCH_PARAM = 'settings_tab'

/** Feature-check variant key for “join an org” access nav (User access first vs domain control). */
export const OBI_HOME_NEW_JOIN_AN_ORG_VARIANT = 'obi_home_new_join_an_org'

/**
 * Legacy path prefix for bookmarks; {@link parseSectionFromSettingsPathname} and the modal
 * migration replace with `?settings=` on the host path.
 */
const SETTINGS_PATH_PREFIX = '/app/settings'

/**
 * Sections whose URL form has a "default tab" applied when callers don't pass an explicit
 * `settingsTab`. Profile opens with sub-routes (General / Login) and admin People with sub-tabs
 * (Active / Pending / Migrating users / Groups / Permissions). Pre-populating `settings_tab=` on
 * the browser URL avoids a second `history.replace` from tab sync (host route re-render).
 *
 * Kept at module scope here (instead of derived from `SETTINGS_MEMORY_ROUTE_TAB_CONFIG`) so
 * `mergeSettingsSearch` stays free of any import from `./settings_modal_memory_tabs.js`, which
 * already imports `SETTINGS_SECTION` from this module — i.e. no circular dependency.
 *
 * Memory-route defaults live in {@link ./settings_modal_memory_tabs.js}
 * `SETTINGS_MEMORY_ROUTE_TAB_CONFIG`. URL defaults here apply only to `mergeSettingsSearch`.
 */
const URL_DEFAULT_TAB_BY_SECTION = Object.freeze({
  profile: 'general',
  ai_notetaker: 'notetaker',
  'admin/people': 'active_users',
  'organization/people': 'active_users',
})

/**
 * Default `settings_tab=` slug applied to URLs by {@link mergeSettingsSearch} when callers don't
 * pass an explicit tab. Returns `null` for sections that don't auto-fill a default.
 *
 * @param {string} section
 * @returns {string | null}
 */
function getUrlDefaultTabForSection(section) {
  return URL_DEFAULT_TAB_BY_SECTION[section] ?? null
}

/**
 * Merge `settings=<section>` into existing search (preserves other query params).
 * When `settingsTab` is omitted, applies {@link getUrlDefaultTabForSection} so the browser URL
 * matches the memory router before first paint.
 *
 * @param {string} section — {@link SETTINGS_SECTION} string value
 * @param {string} [existingSearch] — `location.search` including `?` or empty
 * @param {string} [settingsTab] — optional {@link SETTINGS_TAB_SEARCH_PARAM} slug (memory-route tabs)
 * @returns {string} — search string including leading `?` when non-empty
 */
export const mergeSettingsSearch = (section, existingSearch = '', settingsTab = undefined) => {
  const params = new URLSearchParams(existingSearch)
  params.set(SETTINGS_SEARCH_PARAM, section)
  const resolvedTab = (settingsTab != null && settingsTab !== '')
    ? settingsTab
    : getUrlDefaultTabForSection(section)
  if (resolvedTab != null && resolvedTab !== '') {
    params.set(SETTINGS_TAB_SEARCH_PARAM, resolvedTab)
  } else {
    params.delete(SETTINGS_TAB_SEARCH_PARAM)
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/** @param {string} [existingSearch] */
export const parseSettingsSectionFromSearch = (existingSearch = '') => {
  const raw = new URLSearchParams(existingSearch).get(SETTINGS_SEARCH_PARAM)
  if (raw == null || raw === '') {
    return null
  }
  return raw
}

/**
 * True when settings IA rebrand is on and the location has a non-empty `settings` query param
 * (same signal as {@link ./settings_modal.js} for “modal flow” URL state).
 * @param {{ isRebrandSettingsIaEnabled: boolean, search: string }} args
 */
export function isSettingsModalNavigationActive({ isRebrandSettingsIaEnabled, search }) {
  if (!isRebrandSettingsIaEnabled) {
    return false
  }
  return parseSettingsSectionFromSearch(search) != null
}

/** @param {string} [existingSearch] */
export const parseSettingsTabFromSearch = (existingSearch = '') => {
  const raw = new URLSearchParams(existingSearch).get(SETTINGS_TAB_SEARCH_PARAM)
  if (raw == null || raw === '') {
    return null
  }
  return raw
}

/** Remove `settings` and `settings_tab` from search (e.g. when closing the modal). */
export const stripSettingsSearch = (existingSearch = '') => {
  const params = new URLSearchParams(existingSearch)
  params.delete(SETTINGS_SEARCH_PARAM)
  params.delete(SETTINGS_TAB_SEARCH_PARAM)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const parseSectionFromSettingsPathname = (pathname) => {
  const prefix = `${SETTINGS_PATH_PREFIX}/`
  if (!pathname.startsWith(prefix)) {
    return null
  }
  const encoded = pathname.slice(prefix.length)
  if (!encoded) {
    return null
  }
  return decodeURIComponent(encoded)
}

/** Mobile-only: show group list before picking a section. */
export const SETTINGS_MOBILE_LIST_SENTINEL = '_list'

/**
 * Main-pane render strategy for {@link ./section_content_registry.js} and {@link ./settingsConfig.ts}.
 * @readonly
 * @enum {string}
 */
export const SETTINGS_MODAL_PANE_KIND = Object.freeze({
  COLUMN: 'column',
  MEMORY_ROUTE: 'memory_route',
  USERS_PAGE: 'users_page',
  GROUPS_SHELL: 'groups_shell',
})

/**
 * Values for `?settings=` when the settings modal is open.
 * Keep in sync with tests and {@link ./legacy_admin_routes.js} / admin redirects.
 */
export const SETTINGS_SECTION = {
  profile: 'profile',
  calendar: 'calendar',
  branding: 'branding',
  link: 'link',
  login_preferences: 'login_preferences',
  workflows: 'workflows',
  security: 'security',
  ai_notetaker: 'ai_notetaker',
  /** Callie Features page. URL slug stays `assistant` (`?settings=assistant`). */
  callie: 'assistant',
  contacts_settings: 'contacts_settings',
  adminPeople: 'admin/people',
  adminUsers: 'admin/users',
  adminInvitations: 'admin/invitations',
  adminMigrations: 'admin/migrations',
  adminGroups: 'admin/groups',
  adminDashboard: 'admin/dashboard',
  adminAccess: 'admin/access',
  adminBilling: 'admin/billing',
  /** @deprecated Use {@link SETTINGS_SECTION.adminOrganization}. Kept for legacy `?settings=` URLs. */
  adminBranding: 'admin/branding',
  adminOrganization: 'admin/organization',
  adminPermissions: 'admin/permissions',
  adminSecurity: 'admin/security',
  adminNotetaker: 'admin/notetaker',
  adminManagedEventTypes: 'admin/managed_event_types',
  orgDirectory: 'organization/people',
}

const SETTINGS_ADMIN_PEOPLE_SECTIONS = [
  SETTINGS_SECTION.adminPeople,
]

/** Org admin center sections (excluding the People hub). */
const SETTINGS_ADMIN_ORG_SECTIONS = [
  SETTINGS_SECTION.adminBilling,
  SETTINGS_SECTION.adminDashboard,
  SETTINGS_SECTION.adminAccess,
  SETTINGS_SECTION.adminManagedEventTypes,
  SETTINGS_SECTION.adminOrganization,
  SETTINGS_SECTION.adminSecurity,
]

const SETTINGS_PERSONAL_ACCOUNT_SECTIONS = [
  SETTINGS_SECTION.profile,
  SETTINGS_SECTION.calendar,
  SETTINGS_SECTION.branding,
  SETTINGS_SECTION.link,
  SETTINGS_SECTION.workflows,
  SETTINGS_SECTION.ai_notetaker,
  SETTINGS_SECTION.callie,
  SETTINGS_SECTION.contacts_settings,
  SETTINGS_SECTION.login_preferences,
  SETTINGS_SECTION.security,
  SETTINGS_SECTION.orgDirectory,
]

const SETTINGS_ORG_DIRECTORY_SECTIONS = [
  SETTINGS_SECTION.orgDirectory,
]

/** Personal sections valid in the modal (e.g. `?settings=` deep links) but omitted from the left nav. */
export const SETTINGS_PERSONAL_ACCOUNT_SECTIONS_HIDDEN_FROM_NAV = [
  SETTINGS_SECTION.login_preferences,
]

export const isSettingsAdminPeopleSection = (section) => (
  section === SETTINGS_SECTION.adminPeople
)

export const isSettingsAdminSection = (section) => (
  section != null && (
    isSettingsAdminPeopleSection(section)
    || SETTINGS_ADMIN_ORG_SECTIONS.includes(section)
  )
)

/**
 * Legacy `settings_tab` under `?settings=ai_notetaker` that rewrites to the Callie Features page.
 * Current Notetaker tab slugs live on {@link ./settings_modal_ai_page.js} `SETTINGS_MODAL_AI_TAB`.
 */
export const LEGACY_AI_NOTETAKER_TAB_CALLIE = 'callie'

/**
 * @typedef {object} LegacyTabRewrite
 * @property {string} section — canonical `SETTINGS_SECTION` after rewrite
 * @property {string} [settingsTab] — tab slug to set; use `''` to clear `settings_tab=`
 */

/**
 * Tab-level legacy rewrites: `?settings=<section>&settings_tab=<tab>` → a different section/tab.
 * Checked **before** {@link LEGACY_SECTION_REWRITES} so section+tab pairs can fan out (e.g. old AI
 * Callie tab → Callie Features page).
 *
 * @type {Readonly<Record<string, Readonly<Record<string, LegacyTabRewrite>>>>}
 */
export const LEGACY_SETTINGS_TAB_REWRITES = Object.freeze({
  [SETTINGS_SECTION.ai_notetaker]: Object.freeze({
    [LEGACY_AI_NOTETAKER_TAB_CALLIE]: Object.freeze({
      section: SETTINGS_SECTION.callie,
      settingsTab: '',
    }),
  }),
})

/**
 * @param {string | null | undefined} rawSection
 * @param {string | null | undefined} rawTab
 * @returns {LegacyTabRewrite | null}
 */
export function resolveLegacySettingsTabRewrite(rawSection, rawTab) {
  if (rawSection == null || rawTab == null || rawTab === '') {
    return null
  }
  return LEGACY_SETTINGS_TAB_REWRITES[rawSection]?.[rawTab] ?? null
}

/**
 * @typedef {object} LegacySectionRewrite
 * @property {string} section — current `SETTINGS_SECTION` value the alias resolves to
 * @property {string} [defaultSettingsTab] — set this slug on the rewritten URL (overrides incoming tab)
 * @property {(rawTab: string | null) => string | null} [resolveSettingsTab] — compute the rewritten tab from the incoming tab
 *
 * No tab field set → preserve the incoming `settings_tab=` value (the rewritten section may then
 * apply its own URL default via {@link mergeSettingsSearch}).
 */

/**
 * Single source of truth for legacy `?settings=` aliases that get rewritten to current section ids
 * (and optional default sub-tabs) when the modal renders.
 *
 * These exist for old bookmarks / external links — the IA changed (e.g. the Login admin section is
 * now `admin/access`, the People sub-routes consolidated into a single `admin/people` hub with
 * tabs). Used by:
 *
 * - {@link resolveEffectiveSection} / {@link resolveEffectiveSettingsTab} — pure derivation helpers
 *   that callers run on each render to know which "current" section the URL refers to.
 * - The single rewrite effect in {@link ./use_settings_modal_url_normalization.js} — once per
 *   change of `?settings=`, `history.replace`s the URL to the canonical form so subsequent
 *   navigation, sharing, and tab sync run against the post-rewrite ids.
 *
 * Tab-level pairs (section + tab) live in {@link LEGACY_SETTINGS_TAB_REWRITES} and take precedence.
 *
 * @type {Readonly<Record<string, LegacySectionRewrite>>}
 */
export const LEGACY_SECTION_REWRITES = Object.freeze({
  [SETTINGS_SECTION.adminNotetaker]: {
    section: SETTINGS_SECTION.ai_notetaker,
    defaultSettingsTab: 'notetaker_access',
  },
  'admin/login': { section: SETTINGS_SECTION.adminAccess },
  [SETTINGS_SECTION.adminBranding]: { section: SETTINGS_SECTION.adminOrganization },
  [SETTINGS_SECTION.adminUsers]: {
    section: SETTINGS_SECTION.adminPeople,
    defaultSettingsTab: 'active_users',
  },
  [SETTINGS_SECTION.adminInvitations]: {
    section: SETTINGS_SECTION.adminPeople,
    defaultSettingsTab: 'pending_users',
  },
  [SETTINGS_SECTION.adminMigrations]: {
    section: SETTINGS_SECTION.adminPeople,
    defaultSettingsTab: 'migrating_users',
  },
  [SETTINGS_SECTION.adminGroups]: {
    section: SETTINGS_SECTION.adminPeople,
    defaultSettingsTab: 'groups',
  },
  [SETTINGS_SECTION.adminPermissions]: {
    section: SETTINGS_SECTION.adminPeople,
    /** Permissions sub-tabs were `event_types` / `workflows` / `invitations` / `payments`. */
    resolveSettingsTab: (rawTab) => (
      rawTab && ['event_types', 'workflows', 'invitations', 'payments'].includes(rawTab)
        ? rawTab
        : 'event_types'
    ),
  },
  'organization/users': {
    section: SETTINGS_SECTION.orgDirectory,
    defaultSettingsTab: 'active_users',
  },
  'organization/invitations': {
    section: SETTINGS_SECTION.orgDirectory,
    defaultSettingsTab: 'pending_users',
  },
  'organization/migrations': {
    section: SETTINGS_SECTION.orgDirectory,
    defaultSettingsTab: 'migrating_users',
  },
})

/**
 * Resolve a raw `?settings=` value to the current section id. Pure; safe to call on every render.
 * Returns the input unchanged if no rewrite rule applies. Tab-level rewrites take precedence when
 * `rawTab` is provided.
 *
 * @param {string | null | undefined} rawSection
 * @param {string | null | undefined} [rawTab]
 * @returns {string | null | undefined}
 */
export function resolveEffectiveSection(rawSection, rawTab = null) {
  if (rawSection == null) {
    return rawSection
  }
  const tabRewrite = resolveLegacySettingsTabRewrite(rawSection, rawTab)
  if (tabRewrite != null) {
    return tabRewrite.section
  }
  return LEGACY_SECTION_REWRITES[rawSection]?.section ?? rawSection
}

/**
 * Resolve the effective `settings_tab=` slug for a raw `?settings=` value. For non-aliased
 * sections, returns `rawTab` unchanged. Pure; safe to call on every render. Tab-level rewrites
 * take precedence.
 *
 * @param {string | null | undefined} rawSection
 * @param {string | null | undefined} rawTab
 * @returns {string | null | undefined}
 */
export function resolveEffectiveSettingsTab(rawSection, rawTab) {
  if (rawSection == null) {
    return rawTab
  }
  const tabRewrite = resolveLegacySettingsTabRewrite(rawSection, rawTab)
  if (tabRewrite != null) {
    return tabRewrite.settingsTab === '' ? null : (tabRewrite.settingsTab ?? null)
  }
  const rule = LEGACY_SECTION_REWRITES[rawSection]
  if (rule == null) {
    return rawTab
  }
  if (rule.resolveSettingsTab) {
    return rule.resolveSettingsTab(rawTab ?? null)
  }
  if (rule.defaultSettingsTab != null) {
    return rule.defaultSettingsTab
  }
  return rawTab
}

/**
 * Settings sections recognised by the modal — used to fall back to Profile when the URL contains
 * an unknown section. Includes both current section ids and the legacy aliases above (defense in
 * depth so the modal doesn't briefly render the fallback while the rewrite effect is queued).
 *
 * @type {ReadonlySet<string>}
 */
export const KNOWN_SETTINGS_SECTIONS = new Set([
  ...SETTINGS_PERSONAL_ACCOUNT_SECTIONS,
  ...SETTINGS_ADMIN_PEOPLE_SECTIONS,
  ...SETTINGS_ADMIN_ORG_SECTIONS,
  ...SETTINGS_ORG_DIRECTORY_SECTIONS,
  ...Object.keys(LEGACY_SECTION_REWRITES),
])

/**
 * Whether the user may open `section` in the settings modal (nav visibility + feature gates).
 * Mirrors the access rules in {@link ./use_settings_modal_section.js}.
 *
 * @param {string | null | undefined} section — canonical `SETTINGS_SECTION` value
 * @param {Set<string>} accountEnabledSections
 * @param {Set<string>} adminEnabledSections
 * @returns {boolean}
 */
export function isSettingsSectionAccessible(section, accountEnabledSections, adminEnabledSections) {
  if (section == null || section === SETTINGS_MOBILE_LIST_SENTINEL) {
    return false
  }
  if (!KNOWN_SETTINGS_SECTIONS.has(section)) {
    return false
  }
  if (isSettingsAdminSection(section)) {
    return isSettingsAdminPeopleSection(section)
      ? adminEnabledSections.has(SETTINGS_SECTION.adminPeople)
      : adminEnabledSections.has(section)
  }
  if (SETTINGS_PERSONAL_ACCOUNT_SECTIONS.includes(section)) {
    return accountEnabledSections.has(section)
  }
  return true
}
